import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/** Custom spacing tokens from tailwind.config.ts */
const spacingTokens = ['3xs', '2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'] as const;

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Our composite text-* classes should be treated as font-size overrides
      // so they correctly replace Tailwind's text-xs, text-sm, etc.
      "font-size": [
        { text: [
          "page-title", "title",
          "body-lg", "body-md", "body-sm",
          "value", "value-md", "value-sm",
          "label", "label-sm", "label-lg",
          "card-title",
          "helper", "overline", "mono", "hero",
          "dialog-title", "subtitle",
          "link", "error",
        ]}
      ],
      // Register custom spacing tokens so gap-sm overrides gap-7, etc.
      gap: [{ gap: [...spacingTokens] }],
      "gap-x": [{ "gap-x": [...spacingTokens] }],
      "gap-y": [{ "gap-y": [...spacingTokens] }],
      p: [{ p: [...spacingTokens] }],
      px: [{ px: [...spacingTokens] }],
      py: [{ py: [...spacingTokens] }],
      pt: [{ pt: [...spacingTokens] }],
      pr: [{ pr: [...spacingTokens] }],
      pb: [{ pb: [...spacingTokens] }],
      pl: [{ pl: [...spacingTokens] }],
      m: [{ m: [...spacingTokens] }],
      mx: [{ mx: [...spacingTokens] }],
      my: [{ my: [...spacingTokens] }],
      mt: [{ mt: [...spacingTokens] }],
      mr: [{ mr: [...spacingTokens] }],
      mb: [{ mb: [...spacingTokens] }],
      ml: [{ ml: [...spacingTokens] }],
      "space-x": [{ "space-x": [...spacingTokens] }],
      "space-y": [{ "space-y": [...spacingTokens] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a string to Title Case (first letter of each word capitalized)
 * Example: "ROMAIN CHIPPIER" -> "Romain Chippier"
 */
export function toTitleCase(str: string | null | undefined): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** "08:00:00" or "08:00" → "08h00" */
export function fmtTime(time: string): string {
  return time.slice(0, 5).replace(':', 'h');
}

export function formatNumber(value: number, decimals: number = 1): string {
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/** Format a volume: strip trailing ".0" decimals → 2m³, 2,5m³ */
export function fmtVolume(value: number | null | undefined): string {
  if (value == null) return '0';
  return Number.isInteger(value)
    ? String(value)
    : value.toLocaleString('fr-FR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
}

export function formatNumberForPDF(value: number, decimals: number = 1): string {
  // Format without thousand separator for PDF compatibility
  return value.toFixed(decimals).replace('.', ',');
}
